
import { useState, useEffect, useMemo } from "react";
import { UserRoleManagement } from "@/components/admin/UserRoleManagement";
import { BookManagement } from "@/components/admin/BookManagement";
import { SpecialistManagement } from "@/components/admin/SpecialistManagement";
import { LiveSessionsManagement } from "@/components/admin/LiveSessionsManagement";
import { FeatureMetricsPanel } from "@/components/admin/FeatureMetricsPanel";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useLocation } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";

type TabValue = "users" | "books" | "specialists" | "sessions" | "metrics";

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState<TabValue>("users");
  const location = useLocation();
  
  // Memoize the active tab calculation to prevent unnecessary re-renders
  const calculatedTab = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get("tab");
    if (tab === "books") return "books";
    if (tab === "specialists") return "specialists";
    if (tab === "sessions") return "sessions";
    if (tab === "metrics") return "metrics";
    return "users";
  }, [location.search]);

  // Only update state if the calculated tab is different
  useEffect(() => {
    if (calculatedTab !== activeTab) {
      setActiveTab(calculatedTab as TabValue);
    }
  }, [calculatedTab, activeTab]);
  
  return (
    <TooltipProvider>
      <AdminLayout activeTab={activeTab}>
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">Admin Panel</h1>
            <p className="text-muted-foreground">Manage your system and users</p>
          </div>
          
          {/* Content based on active tab - navigation handled by sidebar */}
          {activeTab === "users" && <UserRoleManagement />}
          {activeTab === "books" && <BookManagement />}
          {activeTab === "specialists" && <SpecialistManagement />}
          {activeTab === "sessions" && <LiveSessionsManagement />}
          {activeTab === "metrics" && <FeatureMetricsPanel />}
        </div>
      </AdminLayout>
    </TooltipProvider>
  );
};

export default AdminPanel;
